// In-course player for the Course + Lessons model.
// Click a course -> chapter-wise lesson outline (sidebar) -> play each lesson inline
// (uploaded video or YouTube embed) -> formative checkpoint question between lessons
// -> final quiz + certificate at the end.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { motion } from 'framer-motion';
import {
    CheckCircle2, ChevronLeft, ChevronRight, PlayCircle, Award, BookOpen, Clock,
    GraduationCap, ListChecks, Lightbulb, Trophy, Loader2, XCircle, Maximize, Lock,
} from 'lucide-react';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

function authToken() {
    try { return localStorage.getItem('utc_token') || ''; } catch { return ''; }
}

export default function TraineeCourseDetail() {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const [mode, setMode] = useState('lesson'); // 'lesson' | 'checkpoint'
    const [marking, setMarking] = useState(false);

    const load = (keepIdx = true) => api.get(`/trainee/course-catalog/${courseId}`).then((r) => {
        setData(r.data);
        if (!keepIdx) {
            const firstUndone = (r.data.lessons || []).findIndex((l) => !l.completed);
            setActiveIdx(firstUndone >= 0 ? firstUndone : 0);
        }
    }).catch(() => setNotFound(true));

    useEffect(() => { load(false); /* eslint-disable-next-line */ }, [courseId]);

    if (notFound) return (
        <div data-testid="course-not-found" className="max-w-md mx-auto text-center py-20">
            <div className="h-14 w-14 rounded-2xl bg-brand-navy/5 grid place-items-center mx-auto text-brand-navy/40"><BookOpen className="h-7 w-7" /></div>
            <h2 className="font-display font-bold text-xl text-white mt-4">Course unavailable</h2>
            <p className="text-sm text-white/70 mt-1">This course may have been unpublished or removed. Please pick another from the catalogue.</p>
            <button onClick={() => navigate('/learn/courses')} className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-yellow text-brand-black font-bold hover:brightness-105 transition" data-testid="course-notfound-back">
                <ChevronLeft className="h-4 w-4" /> Back to courses
            </button>
        </div>
    );
    if (!data) return <div className="py-16 text-center text-white/60"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

    const lessons = data.lessons || [];
    const lesson = lessons[activeIdx];
    const allDone = lessons.length > 0 && lessons.every((l) => l.completed);
    const checkpoint = data.checkpoints?.[String(activeIdx)];

    const markComplete = () => {
        if (!lesson || marking) return;
        const curId = lesson.id;
        const goToCheckpoint = checkpoint && activeIdx < lessons.length - 1;
        // Instant, optimistic advance (doc Point 28): update the UI on the FIRST tap instead of
        // waiting on two sequential network calls (which made the button feel dead and forced
        // users to tap many times). Mark the lesson done locally and move on right away…
        setData((prev) => prev
            ? { ...prev, lessons: (prev.lessons || []).map((l) => l.id === curId ? { ...l, completed: true } : l) }
            : prev);
        if (goToCheckpoint) setMode('checkpoint');
        else goNext();
        // …then persist in the background and reconcile (points, quiz unlock, certificate).
        setMarking(true);
        api.post(`/videos/${curId}/progress`, { last_position: lesson.duration_sec || 0, delta_seconds: 5, completed: true })
            .then(() => load(true))
            .catch(() => { /* already advanced; will re-sync on next action */ })
            .finally(() => setMarking(false));
    };

    const goNext = () => {
        setMode('lesson');
        if (activeIdx < lessons.length - 1) setActiveIdx(activeIdx + 1);
    };

    return (
        <div data-testid="course-detail-page" className="grid lg:grid-cols-12 gap-6">
            {/* Sidebar curriculum */}
            <aside className="lg:col-span-4 xl:col-span-3 order-2 lg:order-1 min-w-0">
                <button onClick={() => navigate('/learn/courses')} className="inline-flex items-center gap-1 text-xs font-bold text-white/70 hover:text-white mb-3 transition" data-testid="course-back-btn">
                    <ChevronLeft className="h-3.5 w-3.5" /> Back to courses
                </button>
                <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden sticky top-24 shadow-[0_2px_12px_rgba(35,31,32,0.06)]">
                    <div className="p-5 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white">
                        <div className="text-[13px] uppercase tracking-[0.2em] text-brand-navy/55 font-bold">{data.category_label || data.category}</div>
                        <div className="font-display font-bold text-lg text-slate-900 leading-snug mt-1 line-clamp-2">{data.title}</div>
                        <div className="mt-4 flex items-center gap-4">
                            <svg viewBox="0 0 60 60" className="w-14 h-14 flex-shrink-0">
                                <circle cx="30" cy="30" r="25" fill="none" stroke="#F1F5F9" strokeWidth="6" />
                                <circle cx="30" cy="30" r="25" fill="none" stroke="#231F20" strokeWidth="6"
                                    strokeLinecap="round" strokeDasharray="157" transform="rotate(-90 30 30)"
                                    style={{ strokeDashoffset: 157 - (157 * data.progress_percent / 100), transition: 'stroke-dashoffset 0.6s ease' }} />
                                <text x="30" y="36" textAnchor="middle" fontSize="13" fontWeight="800" fill="#231F20">{Math.round(data.progress_percent)}%</text>
                            </svg>
                            <div className="flex-1 min-w-0 text-sm">
                                <div className="flex items-center gap-1 text-slate-600"><BookOpen className="h-3.5 w-3.5" /> {data.lesson_count} lesson{data.lesson_count === 1 ? '' : 's'}</div>
                                <div className="flex items-center gap-1 text-slate-600 mt-1"><Award className="h-3.5 w-3.5" /> {data.points_on_complete} pts</div>
                                {data.certificate && <div className="flex items-center gap-1 text-emerald-600 font-semibold mt-1"><GraduationCap className="h-3.5 w-3.5" /> Certified</div>}
                            </div>
                        </div>
                    </div>

                    <div className="max-h-[55vh] overflow-y-auto p-3">
                        <ul className="space-y-1">
                            {lessons.map((l, i) => {
                                const active = i === activeIdx && mode === 'lesson';
                                return (
                                    <li key={l.id}>
                                        <button
                                            onClick={() => { setActiveIdx(i); setMode('lesson'); }}
                                            className={`w-full text-left px-3 py-2.5 rounded-xl text-sm flex items-center gap-2 transition ${active ? 'bg-brand-navy/5 border-l-4 border-brand-yellow text-brand-black font-bold' : l.completed ? 'text-emerald-700 hover:bg-brand-navy/5' : 'text-brand-navy/80 hover:bg-brand-navy/5'}`}
                                            data-testid={`lesson-item-${i}`}>
                                            {l.completed ? <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" /> : <PlayCircle className="h-4 w-4 text-slate-400 flex-shrink-0" />}
                                            <span className="flex-1 truncate">{i + 1}. {l.title}</span>
                                            {l.is_embed && <span className="text-[11px] uppercase text-slate-400">yt</span>}
                                        </button>
                                    </li>
                                );
                            })}
                            {data.quiz?.available && (
                                <li className="pt-1">
                                    <button
                                        onClick={() => navigate(`/learn/quiz/${courseId}`)}
                                        disabled={!allDone}
                                        className={`w-full text-left px-3 py-2.5 rounded-xl text-sm flex items-center gap-2 transition ${data.certificate ? 'bg-emerald-50 text-emerald-700' : allDone ? 'bg-brand-yellow/15 text-brand-black font-bold hover:bg-brand-yellow/25' : 'text-brand-navy/40 bg-brand-navy/5'}`}
                                        data-testid="sidebar-final-quiz-btn">
                                        {data.certificate ? <CheckCircle2 className="h-4 w-4" /> : <Award className="h-4 w-4 text-amber-500" />}
                                        <span className="flex-1 truncate">Final Quiz & Certificate</span>
                                        <span className="text-[12px]">{data.quiz.question_count}Q</span>
                                    </button>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            </aside>

            {/* Content */}
            <div className="lg:col-span-8 xl:col-span-9 order-1 lg:order-2 min-w-0">
                {mode === 'checkpoint' && checkpoint ? (
                    <Checkpoint q={checkpoint} onContinue={goNext} />
                ) : lessons.length === 0 ? (
                    <EmptyState onQuiz={() => navigate(`/learn/quiz/${courseId}`)} quizAvailable={data.quiz?.available} />
                ) : (
                    <LessonView
                        key={lesson.id}
                        lesson={lesson}
                        index={activeIdx}
                        total={lessons.length}
                        canPrev={activeIdx > 0}
                        onPrev={() => { setActiveIdx(Math.max(0, activeIdx - 1)); setMode('lesson'); }}
                        onComplete={markComplete}
                        marking={marking}
                        isLast={activeIdx === lessons.length - 1}
                        allDone={allDone}
                        quizAvailable={data.quiz?.available}
                        certificate={data.certificate}
                        onQuiz={() => navigate(`/learn/quiz/${courseId}`)}
                    />
                )}
            </div>
        </div>
    );
}

function LessonPlayer({ lesson, onWatchedFull }) {
    if (lesson.is_embed) {
        const src = `${lesson.embed_url || lesson.stream_url}?rel=0&modestbranding=1&playsinline=1`;
        return (
            <div className="aspect-video bg-black rounded-3xl overflow-hidden">
                <iframe title={lesson.title} src={src} allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    className="w-full h-full" data-testid="lesson-embed" />
            </div>
        );
    }
    const isUpload = lesson.kind === 'upload';
    const src = isUpload
        ? `${process.env.REACT_APP_BACKEND_URL}${lesson.stream_url}?auth=${encodeURIComponent(authToken())}`
        : lesson.stream_url;
    // Non-skippable enforcement (doc Point 21) applies to uploaded videos only, and only
    // until the learner has watched the whole thing once (lesson.completed).
    const enforce = isUpload && !lesson.completed;
    return <VideoPlayer lessonId={lesson.id} src={src} enforce={enforce} durationSec={lesson.duration_sec || 0} onWatchedFull={onWatchedFull} />;
}

// Custom video player: full-screen button (doc Point 22) + no-skip first watch (doc Point 21).
function VideoPlayer({ lessonId, src, enforce, durationSec = 0, onWatchedFull }) {
    const videoRef = useRef(null);
    const wrapRef = useRef(null);
    const maxRef = useRef(0);        // furthest point actually reached
    const lastRef = useRef(0);       // previous currentTime (to measure real playback)
    const accumRef = useRef(0);      // unsent watched seconds
    const accumTotalRef = useRef(0); // total real-playback seconds (never reset) — for unlock
    const doneRef = useRef(false);
    const [blocked, setBlocked] = useState(false);

    const unlock = () => {
        if (doneRef.current) return;
        doneRef.current = true;
        post(accumRef.current || 1, true);
        accumRef.current = 0;
        onWatchedFull && onWatchedFull();
    };

    const post = (deltaSeconds, completed) => {
        const v = videoRef.current;
        api.post(`/videos/${lessonId}/progress`, {
            last_position: Math.round(v?.currentTime || 0),
            delta_seconds: Math.max(0, Math.round(deltaSeconds)),
            completed: !!completed,
        }).catch(() => {});
    };

    const onTimeUpdate = () => {
        const v = videoRef.current;
        if (!v) return;
        const t = v.currentTime;
        const prev = lastRef.current;
        if (t > prev && t - prev < 2) { accumRef.current += (t - prev); accumTotalRef.current += (t - prev); } // count only real playback
        maxRef.current = Math.max(maxRef.current, t);
        lastRef.current = t;
        if (accumRef.current >= 10) { post(accumRef.current, false); accumRef.current = 0; }
        // Unlock "Continue" once the learner has genuinely watched (near) the whole video.
        // Robust across devices even if the video's reported `duration` is Infinity/NaN or the
        // 'ended' event never fires (common on mobile / iOS) — the culprit behind Point 28.
        const vdur = v.duration;
        const finite = vdur && isFinite(vdur) && vdur > 0;
        const known = finite ? vdur : (durationSec > 0 ? durationSec : 0);
        const reachedEnd = finite && t >= vdur * 0.95;
        const watchedEnough = known > 0 && accumTotalRef.current >= known * 0.9;
        if (reachedEnd || watchedEnough) unlock();
    };

    const onSeeking = () => {
        const v = videoRef.current;
        if (!v || !enforce) { lastRef.current = v ? v.currentTime : 0; return; }
        // Allow rewind; block scrubbing PAST the furthest watched point.
        if (v.currentTime > maxRef.current + 1.5) {
            v.currentTime = maxRef.current;
            setBlocked(true);
            setTimeout(() => setBlocked(false), 1800);
        }
        lastRef.current = v.currentTime;
    };

    const onEnded = () => {
        unlock();
    };

    const goFullscreen = () => {
        const el = wrapRef.current;
        const v = videoRef.current;
        if (el?.requestFullscreen) el.requestFullscreen();
        else if (v?.webkitEnterFullscreen) v.webkitEnterFullscreen(); // iOS Safari
        else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
    };

    return (
        <div ref={wrapRef} className="relative aspect-video bg-black rounded-3xl overflow-hidden group">
            <video
                ref={videoRef}
                src={src}
                controls
                playsInline
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                onTimeUpdate={onTimeUpdate}
                onSeeking={onSeeking}
                onEnded={onEnded}
                className="w-full h-full object-contain bg-black"
                data-testid="lesson-video"
            />
            <button type="button" onClick={goFullscreen}
                className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-black/55 text-white text-xs font-bold backdrop-blur hover:bg-black/75 transition"
                title="Full screen" data-testid="lesson-fullscreen-btn">
                <Maximize className="h-3.5 w-3.5" /> Full screen
            </button>
            {blocked && (
                <div className="absolute inset-0 z-20 grid place-items-center bg-black/40 pointer-events-none" data-testid="lesson-skip-blocked">
                    <div className="px-4 py-2 rounded-xl bg-white/95 text-slate-900 text-sm font-bold shadow-lg">Please watch the full video — you can't skip ahead yet.</div>
                </div>
            )}
        </div>
    );
}

function LessonView({ lesson, index, total, canPrev, onPrev, onComplete, marking, isLast, allDone, quizAvailable, certificate, onQuiz }) {
    // Continue unlocks once the video is fully watched. Already-completed lessons and
    // non-uploaded lessons (YouTube/URL — can't be tracked reliably) are unlocked upfront.
    const needsWatch = lesson.kind === 'upload' && !lesson.completed;
    const [watchedFull, setWatchedFull] = useState(!needsWatch);
    useEffect(() => { setWatchedFull(!(lesson.kind === 'upload' && !lesson.completed)); }, [lesson.id, lesson.completed, lesson.kind]);

    const gotoQuizRow = isLast && allDone && quizAvailable;
    const continueLocked = !watchedFull && !gotoQuizRow;

    return (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-4" data-testid="lesson-view">
            <div className="flex flex-wrap items-center gap-2 text-[12px] uppercase tracking-[0.2em] font-bold">
                <span className="px-2 py-0.5 rounded-full bg-brand-yellow text-slate-900">Lesson {index + 1} / {total}</span>
                {lesson.duration_sec > 0 && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 inline-flex items-center gap-1"><Clock className="h-2.5 w-2.5" /> {Math.round(lesson.duration_sec / 60)} min</span>}
                {lesson.completed && <span className="px-2 py-0.5 rounded-full bg-emerald-500 text-white inline-flex items-center gap-1"><CheckCircle2 className="h-2.5 w-2.5" /> Completed</span>}
            </div>
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-white leading-tight">{lesson.title}</h1>

            <LessonPlayer lesson={lesson} onWatchedFull={() => setWatchedFull(true)} />

            <div className="bg-white rounded-2xl border border-slate-200 p-2.5">
                <div className="flex items-center justify-between gap-3">
                    <button onClick={onPrev} disabled={!canPrev}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 font-semibold disabled:opacity-40 hover:text-slate-900" data-testid="lesson-prev-btn">
                        <ChevronLeft className="h-4 w-4" /> Previous
                    </button>
                    {gotoQuizRow ? (
                        <button onClick={onQuiz}
                            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-brand-navy text-brand-yellow font-bold hover:bg-brand-black shadow-md transition" data-testid="lesson-goto-quiz-btn">
                            {certificate ? <><Trophy className="h-4 w-4" /> Retake quiz</> : <><Award className="h-4 w-4" /> Take final quiz</>}
                        </button>
                    ) : (
                        <div className="flex items-center gap-2">
                            {continueLocked && <span className="hidden sm:inline text-[11px] font-semibold text-slate-400 inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Watch fully to continue</span>}
                            <button onClick={onComplete} disabled={marking || continueLocked}
                                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-xl bg-brand-black text-brand-yellow font-semibold hover:bg-[#0F0D0E] shadow-md transition disabled:opacity-40 disabled:cursor-not-allowed" data-testid="lesson-complete-next-btn">
                                {marking ? <Loader2 className="h-4 w-4 animate-spin" /> : continueLocked ? <><Lock className="h-4 w-4" /> Continue</> : <>Continue <ChevronRight className="h-4 w-4" /></>}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
}

function Checkpoint({ q, onContinue }) {
    const [picked, setPicked] = useState(null);
    const revealed = picked !== null;
    const correct = revealed && picked === q.correct_index;
    return (
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8" data-testid="checkpoint-card">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-yellow/30 text-slate-900 text-xs font-bold uppercase tracking-wide mb-4">
                <ListChecks className="h-4 w-4" /> Quick knowledge check
            </div>
            <h2 className="font-display font-bold text-xl text-slate-900 leading-snug">{q.question}</h2>
            <div className="mt-5 space-y-2.5">
                {q.options.map((opt, i) => {
                    let cls = 'border-slate-200 hover:border-slate-400';
                    if (revealed && i === q.correct_index) cls = 'border-emerald-500 bg-emerald-50';
                    else if (revealed && i === picked) cls = 'border-rose-400 bg-rose-50';
                    return (
                        <button key={i} disabled={revealed} onClick={() => setPicked(i)}
                            className={`w-full text-left px-4 py-3 rounded-2xl border-2 flex items-center gap-3 transition ${cls}`}
                            data-testid={`checkpoint-option-${i}`}>
                            <span className="h-7 w-7 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center text-xs font-extrabold flex-shrink-0">{LETTERS[i]}</span>
                            <span className="text-sm text-slate-800 flex-1">{opt}</span>
                            {revealed && i === q.correct_index && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                            {revealed && i === picked && i !== q.correct_index && <XCircle className="h-5 w-5 text-rose-500" />}
                        </button>
                    );
                })}
            </div>
            {revealed && (
                <div className={`mt-5 p-4 rounded-2xl flex gap-3 ${correct ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                    <Lightbulb className={`h-5 w-5 flex-shrink-0 mt-0.5 ${correct ? 'text-emerald-600' : 'text-amber-600'}`} />
                    <div className="text-sm">
                        <div className={`font-bold ${correct ? 'text-emerald-700' : 'text-amber-700'}`}>{correct ? 'Correct!' : 'Not quite.'}</div>
                        {q.explanation && <div className="text-slate-700 mt-0.5 leading-relaxed">{q.explanation}</div>}
                    </div>
                </div>
            )}
            <div className="mt-6 flex justify-end">
                <button onClick={onContinue}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-brand-black text-brand-yellow font-semibold hover:bg-[#0F0D0E] shadow-md transition" data-testid="checkpoint-continue-btn">
                    {revealed ? 'Continue' : 'Skip'} <ChevronRight className="h-4 w-4" />
                </button>
            </div>
        </motion.div>
    );
}

function EmptyState({ onQuiz, quizAvailable }) {
    return (
        <div className="bg-white rounded-3xl border border-slate-200 p-10 text-center" data-testid="course-empty">
            <BookOpen className="h-10 w-10 text-slate-300 mx-auto" />
            <div className="font-display font-bold text-slate-700 mt-3">No lessons added yet</div>
            <p className="text-sm text-slate-400 mt-1">Lessons for this course are coming soon.</p>
            {quizAvailable && (
                <button onClick={onQuiz} className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-brand-yellow text-brand-black font-semibold" data-testid="empty-quiz-btn">
                    <Award className="h-4 w-4" /> Take quiz
                </button>
            )}
        </div>
    );
}
